// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/Contabil/CentralFiscal.tsx
//  Central Fiscal do cliente: o mesmo faturamento e a mesma análise que o contador vê no
//  Portal do Contador (projeção do Simples, Simples × Presumido, alertas, CFOP/CST/
//  PIS-COFINS/NCM), sobre a empresa do login.
//  Os números vêm do Cofre Fiscal (documentos autorizados copiados dos XMLs).
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { EnviarXmlsBotao, type ResumoEnvio } from '@/features/cofre/EnviarXmlsBotao';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { apiClient } from '@/lib/apiClient';
import { useAuth } from '@/hooks/useAuth';
import { AnaliseFiscalPainel } from '@/features/cofre/AnaliseFiscalPainel';
import type { AnaliseFiscal } from '@/features/cofre/analiseFiscal';
import { FaturamentoPainel } from '@/features/cofre/FaturamentoPainel';
import type { ComparacaoRegime, Faturamento, ParametrosRegime } from '@/features/cofre/faturamento';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ReformaPainel } from '@/features/cofre/ReformaPainel';
import type { ParametrosReforma, ReformaConferencia, ReformaSimulacao } from '@/features/cofre/reforma';
import { competenciaAtual, rotuloCompetencia, ultimasCompetencias } from '@/features/contador/contadorService';

async function enviarXmls(arquivo: File) {
  const form = new FormData();
  form.append('arquivo', arquivo);
  const r = await apiClient.post<ResumoEnvio>('/api/contabil/central-fiscal/upload', form, {
    headers: { 'Content-Type': 'multipart/form-data' }, timeout: 600_000,
  });
  return r.data;
}

async function carregar(competencia: number, anexo?: number) {
  const r = await apiClient.get<AnaliseFiscal>('/api/contabil/central-fiscal/analises', { params: { competencia, anexo } });
  return r.data;
}

async function carregarFaturamento(competencia: number) {
  const r = await apiClient.get<Faturamento>('/api/contabil/central-fiscal/faturamento', { params: { competencia } });
  return r.data;
}

async function simular(competencia: number, p: ParametrosRegime) {
  const r = await apiClient.get<ComparacaoRegime>('/api/contabil/central-fiscal/regimes', { params: { competencia, ...p } });
  return r.data;
}

async function conferirReforma(competencia: number) {
  const r = await apiClient.get<ReformaConferencia>('/api/contabil/central-fiscal/reforma', { params: { competencia } });
  return r.data;
}

async function simularReforma(competencia: number, p: ParametrosReforma) {
  const r = await apiClient.get<ReformaSimulacao>('/api/contabil/central-fiscal/reforma/simulacao', { params: { competencia, ...p } });
  return r.data;
}

export default function CentralFiscal() {
  const { user } = useAuth();
  const [competencia, setCompetencia] = useState(competenciaAtual());
  const qc = useQueryClient();

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Central Fiscal</h1>
          <p className="text-muted-foreground mt-1">
            Simples Nacional, pontos de atenção e resumos fiscais de {user?.empresa?.nomeFantasia ?? user?.empresa?.razaoSocial ?? 'sua empresa'}
          </p>
        </div>
        <div className="sm:ml-auto flex gap-2">
          <EnviarXmlsBotao enviar={enviarXmls} rotulo="Enviar XMLs antigos"
            aoConcluir={() => qc.invalidateQueries({ queryKey: ['central-fiscal'] })} />
          <Select value={String(competencia)} onValueChange={(v) => setCompetencia(Number(v))}>
            <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
            <SelectContent>
              {ultimasCompetencias().map((c) => <SelectItem key={c} value={String(c)}>{rotuloCompetencia(c)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <Tabs defaultValue="faturamento">
        <TabsList>
          <TabsTrigger value="faturamento">Faturamento</TabsTrigger>
          <TabsTrigger value="reforma">Reforma tributária</TabsTrigger>
          <TabsTrigger value="analises">Análises fiscais</TabsTrigger>
        </TabsList>
        <TabsContent value="reforma" className="mt-4">
          <ReformaPainel chave={['central-fiscal', user?.empresa?.id]} competencia={competencia}
            conferir={conferirReforma} simular={simularReforma} />
        </TabsContent>
        <TabsContent value="faturamento" className="mt-4">
          <FaturamentoPainel chave={['central-fiscal', user?.empresa?.id]} competencia={competencia}
            carregar={carregarFaturamento} simular={simular} />
        </TabsContent>
        <TabsContent value="analises" className="mt-4">
          <AnaliseFiscalPainel chave={['central-fiscal', user?.empresa?.id]} competencia={competencia} carregar={carregar} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
